import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import api from '../api/Base';
import InstanceApi from '../api/instance/InstanceApi';
import HostApi from '../api/host/HostApi';
import { TARGET_COLOUR_CODES } from '../component/Constants';

import InstanceTarget from '../types/instance/InstanceTarget';
import HostTarget from '../types/host/HostTarget';

// Give each host a unique chart colour, in list order.
export const colourHosts = (hosts: HostTarget[]): HostTarget[] => {
    hosts.slice(0, TARGET_COLOUR_CODES.length).forEach((host, index) => {
        host.color = TARGET_COLOUR_CODES[index];
    });
    return hosts;
};

const sameMembers = (a: { id: number }[], b: { id: number }[]): boolean => {
    if (a.length !== b.length) return false;
    const ids = new Set(a.map(item => item.id));
    return b.every(item => ids.has(item.id));
};

interface UseTargetRefreshProps {
    enabled: boolean,
    instances: InstanceTarget[],
    setInstances: (instances: InstanceTarget[]) => void,
    hosts: HostTarget[],
    setHosts: (hosts: HostTarget[]) => void,
}

// Instances and hosts load once at startup and are kept current by this
// session's own edits, but another session can add or remove them too. On each
// navigation (the time range and its autorefresh ticks live in the URL) check
// membership and reload a list only when it differs: replacing the lists for
// status churn would re-render every page that depends on them.
export const useTargetRefresh = ({ enabled, instances, setInstances, hosts, setHosts }: UseTargetRefreshProps) => {
    const location = useLocation();
    const instancesRef = useRef(instances);
    instancesRef.current = instances;
    const hostsRef = useRef(hosts);
    hostsRef.current = hosts;
    // Bootstrap has just loaded the lists, so skip the first check.
    const primedRef = useRef(false);

    useEffect(() => {
        if (!enabled) return;
        if (!primedRef.current) {
            primedRef.current = true;
            return;
        }
        let active = true;
        const instancesAtStart = instancesRef.current;
        const hostsAtStart = hostsRef.current;

        // Direct calls so a failed background check stays silent and is not
        // mistaken for an empty list.
        Promise.all([
            api.get<{ id: number }[]>('datasource?full=true'),
            api.get<{ id: number }[]>('host?sort=2+asc'),
        ]).then(async ([latestInstances, latestHosts]) => {
            // Skip if this session changed a list while the check was in flight.
            if (!sameMembers(instancesAtStart, latestInstances.data) && instancesRef.current === instancesAtStart) {
                const reloaded = await InstanceApi.getInstancesWithTags();
                if (active && instancesRef.current === instancesAtStart) setInstances(reloaded);
            }
            if (!sameMembers(hostsAtStart, latestHosts.data) && hostsRef.current === hostsAtStart) {
                const reloaded = colourHosts(await HostApi.getHosts());
                if (active && hostsRef.current === hostsAtStart) setHosts(reloaded);
            }
        }).catch((error: any) => {
            console.warn('Background instance/host check failed.', error);
        });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, location.pathname, location.search]);
};

export default useTargetRefresh;
